import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus,
  Edit,
  Users as UsersIcon,
  X,
  Check,
  Loader2,
  ChevronRight,
  Search,
  Trash2,
  Shield,
  ShieldCheck,
  RefreshCw,
  MoreVertical,
  Copy,
  Key,
  UserCog,
  AlertTriangle,
  Tags,
  Mic,
  BarChart3,
  Activity,
  Phone,
  Server,
  Monitor,
  UserCircle,
  Link2,
  FileText,
  Building2,
  DollarSign,
  Database
} from 'lucide-react'
import { userApi } from '../services/api'
import { useAuth } from '../context/AuthContext'
import SearchableDropdown from '../components/SearchableDropdown'
import './Users.css'

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
  }
}

const tableRowVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.03, duration: 0.2 }
  }),
  exit: { opacity: 0, x: 10, transition: { duration: 0.2 } }
}

function Users() {
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()

  // Data
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('')
  const [filterPermission, setFilterPermission] = useState('')

  // Modal state
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState('add') // 'add', 'edit', 'delete'
  const [itemx, setItemx] = useState({})
  const [hasChanged, setHasChanged] = useState(false)
  const [saving, setSaving] = useState(false)

  // Dropdown menu
  const [activeMenu, setActiveMenu] = useState(null)

  useEffect(() => {
    if (currentUser && currentUser.access_to_users !== true && currentUser.access_to_users !== 1) {
      navigate('/')
      return
    }
    loadUsers()
  }, [currentUser, navigate])

  const loadUsers = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else setLoading(true)

    try {
      const res = await userApi.getAll()
      const data = Array.isArray(res.data) ? res.data : []
      setUsers(data)
    } catch (error) {
      console.error('Failed to load users:', error)
      setUsers([])
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      if (searchQuery) {
        const search = searchQuery.toLowerCase()
        if (!u.username?.toLowerCase().includes(search)) return false
      }
      if (filterPermission) {
        const hasPermission = u[filterPermission] === true || u[filterPermission] === 1
        if (!hasPermission) return false
      }
      return true
    })
  }, [users, searchQuery, filterPermission])

  // Stats
  const stats = useMemo(() => {
    const total = users.length
    const withEngine = users.filter(u => u.access_to_engine === true || u.access_to_engine === 1).length
    const withLicenses = users.filter(u => u.access_to_licenses === true || u.access_to_licenses === 1).length
    const withUsers = users.filter(u => u.access_to_users === true || u.access_to_users === 1).length
    const withMonitor = users.filter(u => u.access_to_monitor === true || u.access_to_monitor === 1).length
    return { total, withEngine, withLicenses, withUsers, withMonitor }
  }, [users])

  const openModal = (mode, item = null) => {
    setModalMode(mode)
    if (item) {
      setItemx(JSON.parse(JSON.stringify(item)))
    } else {
      setItemx({})
    }
    setHasChanged(false)
    setShowModal(true)
    setActiveMenu(null)
  }

  const closeModal = () => {
    setShowModal(false)
    setItemx({})
    setHasChanged(false)
  }

  const evalChange = () => setHasChanged(true)

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%'
    let password = ''
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    setItemx({ ...itemx, password })
    evalChange()
  }

  const generateUsername = () => {
    const adjectives = ['swift', 'bright', 'cool', 'fast', 'smart', 'bold', 'calm', 'keen', 'quick', 'sharp']
    const nouns = ['fox', 'hawk', 'wolf', 'bear', 'eagle', 'tiger', 'lion', 'shark', 'falcon', 'cobra']
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)]
    const noun = nouns[Math.floor(Math.random() * nouns.length)]
    const num = Math.floor(Math.random() * 1000)
    const username = `${adj}_${noun}_${num}`
    setItemx({ ...itemx, username })
    evalChange()
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
  }

  const saveUser = async () => {
    if (!itemx.username || !itemx.password) return

    setSaving(true)

    const object = {
      id: itemx.id || null,
      username: itemx.username,
      password: itemx.password,
      access_to_engine: itemx.access_to_engine || false,
      access_to_stats: itemx.access_to_stats || false,
      access_to_monitor: itemx.access_to_monitor || false,
      access_to_dialers: itemx.access_to_dialers || false,
      access_to_avatars: itemx.access_to_avatars || false,
      access_to_licenses: itemx.access_to_licenses || false,
      access_to_users: itemx.access_to_users || false,
      access_to_servers: itemx.access_to_servers || false,
      access_to_backups: itemx.access_to_backups || false,
      access_to_machines: itemx.access_to_machines || false,
      access_to_clients: itemx.access_to_clients || false,
      access_to_financials: itemx.access_to_financials || false,
      access_to_voicesets: itemx.access_to_voicesets || false,
      access_to_keywords: itemx.access_to_keywords || false,
      access_to_links: itemx.access_to_links || false,
      access_to_templates: itemx.access_to_templates || false,
      access_to_sounds: itemx.access_to_sounds || false
    }

    try {
      if (itemx.id) {
        await userApi.update(itemx.id, object)
      } else {
        await userApi.create(object)
      }
      closeModal()
      loadUsers()
    } catch (error) {
      console.error('Failed to save user:', error)
      alert('Error saving user')
    } finally {
      setSaving(false)
    }
  }

  const deleteUser = async () => {
    if (!itemx.id) return

    setSaving(true)
    try {
      await userApi.delete(itemx.id)
      closeModal()
      loadUsers()
    } catch (error) {
      console.error('Failed to delete user:', error)
      alert('Error deleting user')
    } finally {
      setSaving(false)
    }
  }

  const isChecked = (value) => value === true || value === 1

  const permissionOptions = [
    { key: 'access_to_engine', label: 'Engine', icon: UserCog },
    { key: 'access_to_stats', label: 'Stats', icon: BarChart3 },
    { key: 'access_to_monitor', label: 'Monitor', icon: Activity },
    { key: 'access_to_dialers', label: 'Dialers', icon: Phone },
    { key: 'access_to_avatars', label: 'Avatars', icon: UserCircle },
    { key: 'access_to_licenses', label: 'Licenses', icon: Key },
    { key: 'access_to_users', label: 'Users', icon: UsersIcon },
    { key: 'access_to_servers', label: 'Servers', icon: Server },
    { key: 'access_to_backups', label: 'Backups', icon: Database },
    { key: 'access_to_machines', label: 'Machines', icon: Monitor },
    { key: 'access_to_clients', label: 'Clients', icon: Building2 },
    { key: 'access_to_financials', label: 'Financials', icon: DollarSign },
    { key: 'access_to_voicesets', label: 'Voice Sets', icon: Mic },
    { key: 'access_to_keywords', label: 'Keywords', icon: Tags },
    { key: 'access_to_links', label: 'Links', icon: Link2 },
    { key: 'access_to_templates', label: 'Templates', icon: FileText },
    { key: 'access_to_sounds', label: 'Sounds', icon: Shield }
  ]

  // For SearchableDropdown
  const permissionDropdownOptions = useMemo(() =>
    permissionOptions.map(p => ({ id: p.key, name: p.label })),
    []
  )

  const getPermissionCount = (user) => {
    return permissionOptions.filter(p => isChecked(user[p.key])).length
  }

  return (
    <motion.div
      className="users-page"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb */}
      <motion.div className="breadcrumb" variants={itemVariants}>
        <Link to="/">Dashboard</Link>
        <ChevronRight size={16} />
        <span>Users</span>
      </motion.div>

      {/* Header */}
      <motion.div className="page-header" variants={itemVariants}>
        <div>
          <h1 className="page-title">
            <UsersIcon size={28} />
            Users
          </h1>
          <p className="page-subtitle">Manage user accounts and permissions</p>
        </div>
        <div className="header-actions">
          <motion.button
            className="btn btn-ghost btn-icon"
            onClick={() => loadUsers(true)}
            disabled={refreshing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Refresh"
          >
            <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
          </motion.button>
          <motion.button
            className="btn btn-primary"
            onClick={() => openModal('add')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus size={16} /> Add User
          </motion.button>
        </div>
      </motion.div>

      {/* Stats Blocks */}
      <motion.div className="stat-blocks" variants={itemVariants}>
        <div className="stat-block blue">
          <div className="stat-block-icon"><UsersIcon size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{stats.total}</span>
            <span className="stat-block-label">Total Users</span>
          </div>
        </div>
        <div className="stat-block orange">
          <div className="stat-block-icon"><UserCog size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{stats.withEngine}</span>
            <span className="stat-block-label">Engine Access</span>
          </div>
        </div>
        <div className="stat-block cyan">
          <div className="stat-block-icon"><Key size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{stats.withLicenses}</span>
            <span className="stat-block-label">License Access</span>
          </div>
        </div>
        <div className="stat-block purple">
          <div className="stat-block-icon"><Activity size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{stats.withMonitor}</span>
            <span className="stat-block-label">Monitor Access</span>
          </div>
        </div>
        <div className="stat-block green">
          <div className="stat-block-icon"><ShieldCheck size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{stats.withUsers}</span>
            <span className="stat-block-label">Admin Access</span>
          </div>
        </div>
      </motion.div>

      {/* Toolbar */}
      <motion.div className="toolbar" variants={itemVariants}>
        <div className="toolbar-left">
          <div className="input-with-icon">
            <Search size={16} className="icon" />
            <input
              type="text"
              className="input"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <SearchableDropdown
            options={permissionDropdownOptions}
            value={filterPermission}
            onChange={setFilterPermission}
            placeholder="Search permissions..."
            allLabel="All Permissions"
          />
        </div>
        <div className="toolbar-right">
          <span className="results-count">{filteredUsers.length} users</span>
        </div>
      </motion.div>

      {/* Users Table */}
      <motion.div className="table-container" variants={itemVariants}>
        {loading ? (
          <div className="loading-state">
            <Loader2 size={32} className="spin" />
            <p>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <UsersIcon size={32} />
            </div>
            <h3>No users found</h3>
            <p>{searchQuery || filterPermission ? 'Try adjusting your filters' : 'Add your first user to get started'}</p>
            {!searchQuery && !filterPermission && (
              <button className="btn btn-primary" onClick={() => openModal('add')}>
                <Plus size={16} /> Add User
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th className="text-center hide-mobile" title="Total Permissions">#</th>
                  {permissionOptions.map(p => (
                    <th key={p.key} className="text-center perm-icon-col" title={p.label}>
                      <p.icon size={14} />
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="popLayout">
                  {filteredUsers.map((u, index) => (
                    <motion.tr
                      key={u.id}
                      variants={tableRowVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      custom={index}
                      layout
                    >
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar-mini">
                            {u.username?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div className="user-info-cell">
                            <span className="username">{u.username}</span>
                            <span className="user-id">ID: {u.id}</span>
                          </div>
                        </div>
                      </td>
                      <td className="text-center hide-mobile">
                        <span className="permission-count">
                          {getPermissionCount(u)}/{permissionOptions.length}
                        </span>
                      </td>
                      {permissionOptions.map(p => (
                        <td key={p.key} className="text-center">
                          {isChecked(u[p.key]) ? (
                            <span className="permission-badge active"><Check size={12} /></span>
                          ) : (
                            <span className="permission-badge"><X size={12} /></span>
                          )}
                        </td>
                      ))}
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="actions-cell">
                          <button
                            className="btn btn-icon btn-ghost"
                            onClick={(e) => {
                              if (activeMenu?.id === u.id) {
                                setActiveMenu(null)
                                return
                              }
                              const rect = e.currentTarget.getBoundingClientRect()
                              const spaceBelow = window.innerHeight - rect.bottom
                              const shouldOpenUp = spaceBelow < 180
                              setActiveMenu({
                                id: u.id,
                                user: u,
                                top: shouldOpenUp ? rect.top - 140 : rect.bottom + 4,
                                left: rect.left - 160
                              })
                            }}
                          >
                            <MoreVertical size={16} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>

            {/* Fixed position dropdown menu - rendered outside table */}
            <AnimatePresence>
              {activeMenu && (
                <motion.div
                  className="dropdown-menu-fixed"
                  style={{
                    position: 'fixed',
                    top: activeMenu.top,
                    left: activeMenu.left,
                    zIndex: 9999
                  }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                >
                  <button onClick={() => { openModal('edit', activeMenu.user); setActiveMenu(null) }}>
                    <Edit size={14} /> Edit User
                  </button>
                  <button onClick={() => { copyToClipboard(activeMenu.user.username); setActiveMenu(null) }}>
                    <Copy size={14} /> Copy Username
                  </button>
                  <div className="dropdown-divider" />
                  <button
                    className="danger"
                    onClick={() => { openModal('delete', activeMenu.user); setActiveMenu(null) }}
                  >
                    <Trash2 size={14} /> Delete User
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </motion.div>

      {/* Menu backdrop */}
      {activeMenu && <div className="menu-backdrop" onClick={() => setActiveMenu(null)} />}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {modalMode === 'delete' ? (
                <>
                  <div className="modal-header danger">
                    <div className="modal-icon-wrapper danger">
                      <AlertTriangle size={24} />
                    </div>
                    <h2 className="modal-title">Delete User</h2>
                    <button className="modal-close" onClick={closeModal}>
                      <X size={20} />
                    </button>
                  </div>
                  <div className="modal-body">
                    <div className="delete-warning">
                      <p>Are you sure you want to delete user <strong>{itemx.username}</strong>?</p>
                      <p className="text-muted">This action cannot be undone. All data associated with this user will be permanently removed.</p>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={closeModal}>
                      Cancel
                    </button>
                    <motion.button
                      className="btn btn-danger"
                      onClick={deleteUser}
                      disabled={saving}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {saving ? <Loader2 size={16} className="spin" /> : <Trash2 size={16} />}
                      Delete User
                    </motion.button>
                  </div>
                </>
              ) : (
                <>
                  <div className="modal-header">
                    <div className="modal-icon-wrapper">
                      {modalMode === 'add' ? <Plus size={24} /> : <Edit size={24} />}
                    </div>
                    <h2 className="modal-title">
                      {modalMode === 'add' ? 'Add New User' : 'Edit User'}
                    </h2>
                    <button className="modal-close" onClick={closeModal}>
                      <X size={20} />
                    </button>
                  </div>
                  <div className="modal-body">
                    {/* Username */}
                    <div className="input-group">
                      <label className="input-label">Username</label>
                      <div className="input-row">
                        <input
                          type="text"
                          className="input"
                          placeholder="Enter username"
                          value={itemx.username || ''}
                          onChange={(e) => { setItemx({ ...itemx, username: e.target.value }); evalChange() }}
                        />
                        <button
                          className="btn btn-secondary"
                          onClick={generateUsername}
                          type="button"
                          title="Generate random username"
                        >
                          <RefreshCw size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Password */}
                    <div className="input-group">
                      <label className="input-label">Password</label>
                      <div className="input-row">
                        <input
                          type="text"
                          className="input"
                          placeholder="Enter password"
                          value={itemx.password || ''}
                          onChange={(e) => { setItemx({ ...itemx, password: e.target.value }); evalChange() }}
                        />
                        <button
                          className="btn btn-secondary"
                          onClick={generatePassword}
                          type="button"
                          title="Generate random password"
                        >
                          <RefreshCw size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Permissions Grid */}
                    <div className="input-group">
                      <label className="input-label">Permissions</label>
                      <div className="permissions-grid">
                        {permissionOptions.map(perm => (
                          <motion.label
                            key={perm.key}
                            className={`permission-toggle ${isChecked(itemx[perm.key]) ? 'active' : ''}`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked(itemx[perm.key])}
                              onChange={(e) => {
                                setItemx({ ...itemx, [perm.key]: e.target.checked })
                                evalChange()
                              }}
                            />
                            <span className="permission-checkbox">
                              {isChecked(itemx[perm.key]) && <Check size={12} />}
                            </span>
                            <perm.icon size={16} />
                            <span>{perm.label}</span>
                          </motion.label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={closeModal}>
                      Cancel
                    </button>
                    <motion.button
                      className="btn btn-primary"
                      onClick={saveUser}
                      disabled={saving || !itemx.username || !itemx.password}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
                      {modalMode === 'add' ? 'Create User' : 'Save Changes'}
                    </motion.button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default Users